import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { TNil } from '@app/app.interface';
import { IApiError } from '@core/interfaces/api-error.interface';
import { ErrorFormatterService } from '@core/services/error-formatter.service';
import { UserInvitationService } from '@core/services/user-invitation.service';
import { ComponentStore } from '@ngrx/component-store';
import { EMPTY, MonoTypeOperatorFunction, Observable, pipe } from 'rxjs';
import { catchError, switchMap, tap } from 'rxjs/operators';

interface IAcceptCreatorInvitationState {
  alreadyAccepted: boolean;
  token: string | TNil;
  loading: boolean;
  error: string | null;
}

const initialState: IAcceptCreatorInvitationState = {
  alreadyAccepted: false,
  error: null,
  loading: true,
  token: null,
};

@Injectable()
export class AcceptCreatorInvitationStore extends ComponentStore<IAcceptCreatorInvitationState> {
  public readonly token$ = this.selectSignal((state: IAcceptCreatorInvitationState) => state.token);
  public readonly loading$ = this.selectSignal((state: IAcceptCreatorInvitationState) => state.loading);
  public readonly error$ = this.selectSignal((state: IAcceptCreatorInvitationState) => state.error);
  public readonly alreadyAccepted$ = this.selectSignal((state: IAcceptCreatorInvitationState) => state.alreadyAccepted);

  private readonly handleError = (errorMessage: string): MonoTypeOperatorFunction<unknown> =>
    pipe(
      catchError((error: HttpErrorResponse) => {
        const errorCode = this.errorFormatterService.getErrorDetails(error.error as IApiError)?.[0]?.code;

        this.patchState({
          alreadyAccepted: errorCode === 'USER_ALREADY_CREATED',
          error: errorMessage,
          loading: false,
        });

        return EMPTY;
      }),
    );

  public readonly acceptInvitation = this.effect((trigger$: Observable<{ invitationId: string }>) => {
    return trigger$.pipe(
      tap(() => this.patchState({ alreadyAccepted: false, error: null, loading: true })),
      switchMap(({ invitationId }) =>
        this.userInvitationService.acceptInvitation(invitationId).pipe(
          tap((token: string) => {
            this.patchState({ loading: false, token });
          }),
          this.handleError('Failed to accept invitation'),
        ),
      ),
    );
  });

  constructor(
    private readonly userInvitationService: UserInvitationService,
    private readonly errorFormatterService: ErrorFormatterService,
  ) {
    super(initialState);
  }
}
